import React, { ReactElement, useState } from 'react';
import {
  Button, Col, FormGroup, Label, LoaderButton, Row, Textarea,
} from 'idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import { ObalMaterialuNrSrBezchybnostFormData } from '../../../model/pracovnyKrok/ObalMaterialuNrSrBezchybnostFormData';
import { ClientResponse } from '../../../model/ClientResponse';
import LegislativnyMaterialDetail
  from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import routes from '../../../routing/routes';
import { useClient } from '../../../context/ClientStatusContext';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import Uuid from '../../../model/Uuid';
import { usePodpisPredsedaFormDataContext } from '../../../context/PodpisPredsedaFormDataContext';
import ObalMaterialuNrSrComponent
  from '../../SprievodnyDokument/CreateFromForm/ObalMaterialu/component/ObalMaterialuNrSrComponent';
import './obalMaterialuNrSrBezchybnost.scss';
import { ClientStatus } from '../../../model/ClientStatus';
import ObalMaterialuNrSrZaBezchybnostFillSystemDataButton from './ObalMaterialuNrSrZaBezchybnostFillSystemDataButton';
import useFieldErrorMessages from '../../useFieldErrorMessages';

interface Props {
  material: LegislativnyMaterialDetail,
  initFormData: ObalMaterialuNrSrBezchybnostFormData;
  onSubmitForm: (uuid: Uuid, formData: ObalMaterialuNrSrBezchybnostFormData) => Promise<ClientResponse<Blob>>
  fillSystemDataClient(...args): Promise<ClientResponse<ObalMaterialuNrSrBezchybnostFormData>>;
  fillSystemData: ObalMaterialuNrSrBezchybnostFormData;
  fillSystemDataStatus: ClientStatus;
}
export const ObalMaterialuNrSrBezchybnost = ({
  material,
  initFormData,
  onSubmitForm,
  fillSystemDataClient,
  fillSystemData,
  fillSystemDataStatus,
}: Props): ReactElement => {
  const { setGeneratedFile, setObalMaterialuFormData } = usePodpisPredsedaFormDataContext();
  const [obalMaterialu, setObalMaterialu] = useState<ObalMaterialuNrSrBezchybnostFormData>(initFormData);
  const { displaySuccess } = useSuccessMessageContext();
  const navigate = useNavigate();
  const {
    type,
    year,
    index,
    pracovnyKrokId,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, pracovnyKrokId: string }>();
  const updateField: SafeParameterSetter<ObalMaterialuNrSrBezchybnostFormData> = (fieldName, value) => {
    setObalMaterialu(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const {
    getFieldErrorMessage,
  } = useFieldErrorMessages();

  const {
    wrapClient: submitObalMaterialu,
    status,
  } = useClient(onSubmitForm);

  const onSubmit = async (): Promise<void> => {
    const { data } = await submitObalMaterialu(material.uuid, obalMaterialu);
    setObalMaterialuFormData(obalMaterialu);
    setGeneratedFile(new File(
      [data],
      `${type}-${year}-${index}_Obal materiálu NR SR za bezchybnosť.docx`,
    ));
    navigate(routes.procesy.podpisPredseda.withParams(type, year, index, pracovnyKrokId));
    displaySuccess('Dokument bol úspešne uložený');
  };

  return (
    <>
      <ObalMaterialuNrSrZaBezchybnostFillSystemDataButton
        setObalMaterialu={setObalMaterialu}
        fillSystemDataStatus={fillSystemDataStatus}
        fillSystemData={fillSystemData}
        fillSystemDataClient={fillSystemDataClient}
      />
      <section className="obal-materialu-nrsr-bezchybnost">
        <Row>
          <Col width="one-half">
            <FormGroup error={!!getFieldErrorMessage('zodpovednyPredkladatel')}>
              <Label htmlFor="zodpovedny-predkladatel">Zodpovedný predkladateľ
                <span className="not-required"> (nepovinný údaj)</span>
              </Label>
              <span id="hint" className="idsk-hint">Vysledný vygenerovaný dokument má limitovanú výšku.
                Ak bude text obsahovať veľa riadkov, nemusí sa do dokumentu zmestiť celý.
              </span>
              <Textarea
                error={getFieldErrorMessage('zodpovednyPredkladatel')}
                id="podnet"
                type="text"
                name="podnet"
                value={obalMaterialu.zodpovednyPredkladatel}
                onChange={({ target }) => updateField && updateField('zodpovednyPredkladatel', target.value)}
                rows={4}
                maxLength={512}
                showRemaining
                expandHeight
              />
            </FormGroup>
          </Col>
          <Col width="one-half">
            <FormGroup error={!!getFieldErrorMessage('zaBezchybnost')}>
              <Label htmlFor="za-bezchybnost">Za bezchybnosť
                <span className="not-required"> (nepovinný údaj)</span>
              </Label>
              <span id="hint" className="idsk-hint">Vysledný vygenerovaný dokument má limitovanú výšku.
                Ak bude text obsahovať veľa riadkov, nemusí sa do dokumentu zmestiť celý.
              </span>
              <Textarea
                error={getFieldErrorMessage('zaBezchybnost')}
                id="zaBezchybnost"
                type="text"
                name="zaBezchybnost"
                value={obalMaterialu.zaBezchybnost}
                onChange={({ target }) => updateField && updateField('zaBezchybnost', target.value)}
                rows={4}
                maxLength={512}
                showRemaining
                expandHeight
              />
            </FormGroup>
          </Col>
        </Row>
      </section>
      <ObalMaterialuNrSrComponent obalMaterialu={obalMaterialu} updateField={updateField} />
      <FormGroup>
        <ConfirmModal
          question="Naozaj si prajete uložiť formulár a vytvoriť dokument?"
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              loading={status.loading}
              type="submit"
              size="large"
              disableOutsideClickWhileLoading
            >Uložiť
            </LoaderButton>
          )}
          doAfterConfirm={onSubmit}
        />
        <Button
          className="obal_materialu__buttons"
          type="button"
          buttonStyle="third"
          size="large"
          disabled={status.loading}
          onClick={() => navigate(
            routes.procesy.podpisPredseda.withParams(type, year, index, pracovnyKrokId),
          )}
        >Späť
        </Button>
      </FormGroup>
    </>
  );
};

export default ObalMaterialuNrSrBezchybnost;
